import type { Database as Interface, Storage } from "../../design/services";
import type { Config, Entity, Model } from "../../design/types";
import { depopulate } from "./shared";


/** Subscription for a single entity */
interface EntitySubscription {
    callback: (entity: Entity) => void;
    populate?: string[];
    model: Model;
}

/** Subscription for a list query */
interface ListSubscription {
    callback: (entities: Entity[]) => void;
    populate?: string[];
    filter?: Record<string, unknown>;
    order?: { by: string; direction: "asc" | "desc" };
    options?: { cursor?: string; limit?: number };
    model: Model;
}

export class Database implements Interface {
    private store: Map<string, Map<string, Entity>>;
    private stagingStore: Map<string, Map<string, Entity>>;
    private stagingFileUrls: Map<string, Set<string>>;
    private storage: Storage;
    private projectId: string;

    /** Subscriptions for single entities, keyed by "projectId:modelPath:entityId" */
    private entitySubscriptions: Map<string, Set<EntitySubscription>>;

    /** Subscriptions for list queries, keyed by "projectId:modelPath" */
    private listSubscriptions: Map<string, Set<ListSubscription>>;

    conditions: {
        read: Record<string, Record<string, unknown>>;
        write: Record<string, Record<string, unknown>>;
    } = { read: {}, write: {} };

    models: Model[] = [];

    constructor(storage: Storage, projectId: string, _config?: Config) {
        this.storage = storage;
        this.projectId = projectId;
        this.store = new Map();
        this.stagingStore = new Map();
        this.stagingFileUrls = new Map();
        this.entitySubscriptions = new Map();
        this.listSubscriptions = new Map();
    }
    ["constructor"](storage: Storage, projectId: string, config?: Config): Interface {
        return new Database(storage, projectId, config);
    }

    private storeKey(modelPath: string): string {
        return `${this.projectId}:${modelPath}`;
    }

    private stagingFileKey(storeKey: string, entityId: string): string {
        return `${storeKey}:${entityId}`;
    }

    setModels(models: Model[]): void {
        this.models = models;
    }

    private resolveModel(model: Model | string): Model {
        if (typeof model !== 'string') return model;
        const path = model.startsWith('/') ? model : '/' + model;
        const found = this.models.find(m => m.path === path);
        if (!found) throw new Error(`Model not found: '${model}'`);
        return found;
    }

    setConditions(conditions: { read: Record<string, Record<string, unknown>>; write: Record<string, Record<string, unknown>> }): void {
        this.conditions = conditions;
        // Re-notify all active subscribers with new conditions
        for (const [key, subs] of this.listSubscriptions) {
            if (subs.size > 0) this.notifyListSubscribers(key);
        }
        for (const [key, subs] of this.entitySubscriptions) {
            if (subs.size > 0) this.notifyEntitySubscribers(key);
        }
    }

    /**
     * Checks if a record matches a filter object.
     * Supports scalar equality, $in, and $contains operators.
     */
    private matchesFilter(record: Record<string, unknown>, filter?: Record<string, unknown>): boolean {
        if (!filter) return true;
        return Object.entries(filter).every(([key, value]) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                const op = value as Record<string, unknown>;
                if ('$in' in op) {
                    const inValues = op.$in;
                    if (!Array.isArray(inValues)) return false;
                    return inValues.includes(record[key]);
                }
                if ('$contains' in op) {
                    const fieldValue = record[key];
                    if (!Array.isArray(fieldValue)) return false;
                    return fieldValue.includes(op.$contains);
                }
            }
            return record[key] === value;
        });
    }

    async create(model: Model | string, data: Record<string, unknown>, id?: string, staging?: boolean): Promise<Entity> {
        model = this.resolveModel(model);
        data = depopulate(model, data);
        const key = this.storeKey(model.path);

        // Generate id: explicit arg > data.id > random UUID
        const recordId = id ?? (typeof data.id === 'string' ? data.id : crypto.randomUUID());

        if (staging) {
            // Staging create: check duplicates in staging only
            if (!this.stagingStore.has(key)) {
                this.stagingStore.set(key, new Map());
            }
            const stagingCollection = this.stagingStore.get(key)!;

            if (stagingCollection.has(recordId)) {
                throw new Error(`Record with id '${recordId}' already exists in model '${model.name}'`);
            }

            // Process file uploads (staging)
            const [processedData, fileUrls] = await this.processFiles(data, true);

            const record = {
                ...processedData,
                id: recordId,
                _createdAt: new Date().toISOString(),
                _updatedAt: new Date().toISOString()
            } as Entity;

            // Staging skips write conditions — they're enforced on commit()
            // const writeCondition = this.conditions.write[model.name];
            // if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
            //     throw new Error(`Write denied: record does not match condition for model '${model.name}'`);
            // }

            stagingCollection.set(recordId, record);

            // Track file URLs
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, recordId);
                if (!this.stagingFileUrls.has(fileKey)) {
                    this.stagingFileUrls.set(fileKey, new Set());
                }
                for (const url of fileUrls) {
                    this.stagingFileUrls.get(fileKey)!.add(url);
                }
            }

            return record;
        }

        // Check staging first — if entity exists in staging, merge and commit
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(recordId);
        if (stagingRecord) {
            const [processedData, fileUrls] = await this.processFiles(data, true);
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, recordId);
                if (!this.stagingFileUrls.has(fileKey)) this.stagingFileUrls.set(fileKey, new Set());
                for (const url of fileUrls) this.stagingFileUrls.get(fileKey)!.add(url);
            }
            stagingCollection!.set(recordId, { ...stagingRecord, ...processedData, _updatedAt: new Date().toISOString() } as Entity);
            await this.commit(model, recordId);
            return this.store.get(key)!.get(recordId)!;
        }

        // Non-staging create: check duplicates in real store only
        if (!this.store.has(key)) {
            this.store.set(key, new Map());
        }

        const collection = this.store.get(key)!;

        if (collection.has(recordId)) {
            throw new Error(`Record with id '${recordId}' already exists in model '${model.name}'`);
        }

        // Process file uploads
        const [processedData] = await this.processFiles(data);

        // Create the record with metadata (id after spread to prevent data.id override)
        const record = {
            ...processedData,
            id: recordId,
            _createdAt: new Date().toISOString(),
            _updatedAt: new Date().toISOString()
        } as Entity;

        // Check write condition before storing
        const writeCondition = this.conditions.write[model.name];
        if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
            throw new Error(`Write denied: record does not match condition for model '${model.name}'`);
        }

        collection.set(recordId, record);

        // Notify list subscribers that a new entity was added
        this.notifyListSubscribers(this.storeKey(model.path));

        return record;
    }

    get(model: Model | string, id: string, populate?: string[]): Promise<Entity>;
    get(model: Model | string, id: string, populate: string[] | undefined, subscribe: (entity: Entity) => void): () => void;
    get(model: Model | string, id: string, populate?: string[], subscribe?: (entity: Entity) => void): Promise<Entity> | (() => void) {
        model = this.resolveModel(model);
        // For subscriptions, handle synchronously and return unsubscribe
        if (subscribe) {
            const key = `${this.storeKey(model.path)}:${id}`;
            const subscription: EntitySubscription = {
                callback: subscribe,
                populate,
                model
            };

            if (!this.entitySubscriptions.has(key)) {
                this.entitySubscriptions.set(key, new Set());
            }
            this.entitySubscriptions.get(key)!.add(subscription);

            // Fetch and call immediately
            this.fetchEntity(model, id, populate).then(subscribe);

            // Return unsubscribe function
            return () => {
                this.entitySubscriptions.get(key)?.delete(subscription);
            };
        }

        // For non-subscriptions, return promise
        return this.fetchEntity(model, id, populate);
    }

    private async fetchEntity(model: Model, id: string, populate?: string[]): Promise<Entity> {
        const key = this.storeKey(model.path);

        // Check staging first, then real store
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(id);

        if (stagingRecord) {
            // Staging skips read conditions — entity is being edited, always accessible
            // const readCondition = this.conditions.read[model.name];
            // if (readCondition && !this.matchesFilter(stagingRecord as unknown as Record<string, unknown>, readCondition)) {
            //     throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
            // }

            let result: Entity = structuredClone(stagingRecord);
            if (populate && populate.length > 0) {
                result = await this.populateRecord(result, populate, model);
            }
            return result;
        }

        const collection = this.store.get(key);
        const record = collection?.get(id);

        if (!record) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Check read condition — throw "not found" if filtered out (no information leakage)
        const readCondition = this.conditions.read[model.name];
        if (readCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, readCondition)) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Populate relationships if requested
        let result: Entity = structuredClone(record);
        if (populate && populate.length > 0) {
            result = await this.populateRecord(result, populate, model);
        }

        return result;
    }

    list(
        model: Model | string,
        populate?: string[],
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        options?: { cursor?: string; limit?: number }
    ): Promise<Entity[]>;
    list(
        model: Model | string,
        populate: string[] | undefined,
        filter: Record<string, unknown> | undefined,
        order: { by: string; direction: "asc" | "desc" } | undefined,
        options: { cursor?: string; limit?: number } | undefined,
        subscribe: (entities: Entity[]) => void
    ): () => void;
    list(
        model: Model | string,
        populate?: string[],
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        options?: { cursor?: string; limit?: number },
        subscribe?: (entities: Entity[]) => void
    ): Promise<Entity[]> | (() => void) {
        model = this.resolveModel(model);
        // For subscriptions, handle synchronously and return unsubscribe
        if (subscribe) {
            const subscription: ListSubscription = {
                callback: subscribe,
                populate,
                filter,
                order,
                options,
                model
            };

            const listKey = this.storeKey(model.path);
            if (!this.listSubscriptions.has(listKey)) {
                this.listSubscriptions.set(listKey, new Set());
            }
            this.listSubscriptions.get(listKey)!.add(subscription);

            // Fetch and call immediately
            this.fetchList(model, populate, filter, order, options).then(subscribe);

            // Return unsubscribe function
            return () => {
                this.listSubscriptions.get(listKey)?.delete(subscription);
            };
        }

        // For non-subscriptions, return promise
        return this.fetchList(model, populate, filter, order, options);
    }

    private async fetchList(
        model: Model,
        populate?: string[],
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        options?: { cursor?: string; limit?: number }
    ): Promise<Entity[]> {
        const key = this.storeKey(model.path);

        // Build merged Map: real store first, then staging wins on collision
        const merged = new Map<string, Entity>();

        const realCollection = this.store.get(key);
        if (realCollection) {
            for (const [id, entity] of realCollection) {
                merged.set(id, entity);
            }
        }

        const stagingCollection = this.stagingStore.get(key);
        if (stagingCollection) {
            for (const [id, entity] of stagingCollection) {
                merged.set(id, entity);
            }
        }

        if (merged.size === 0) {
            return [];
        }

        // Get all records
        let records: Entity[] = Array.from(merged.values());

        // Merge mandatory read condition with call-site filter (mandatory wins)
        const readCondition = this.conditions.read[model.name];
        const mergedFilter = readCondition ? { ...filter, ...readCondition } : filter;

        // Apply filters
        if (mergedFilter) {
            records = records.filter(record =>
                this.matchesFilter(record as unknown as Record<string, unknown>, mergedFilter)
            );
        }

        // Apply sorting
        if (order) {
            records.sort((a, b) => {
                const aRec = a as unknown as Record<string, unknown>;
                const bRec = b as unknown as Record<string, unknown>;
                const aVal = aRec[order.by];
                const bVal = bRec[order.by];

                // Handle null/undefined values
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return order.direction === "asc" ? -1 : 1;
                if (bVal == null) return order.direction === "asc" ? 1 : -1;

                // Compare based on type
                if (typeof aVal === "string" && typeof bVal === "string") {
                    return order.direction === "asc"
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }
                if (typeof aVal === "number" && typeof bVal === "number") {
                    return order.direction === "asc" ? aVal - bVal : bVal - aVal;
                }

                // Fallback: convert to string for comparison
                const aStr = String(aVal);
                const bStr = String(bVal);
                return order.direction === "asc"
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            });
        }

        // Apply cursor-based pagination
        if (options?.cursor) {
            const cursorIndex = records.findIndex(r => r.id === options.cursor);
            if (cursorIndex !== -1) {
                records = records.slice(cursorIndex + 1);
            }
        }

        // Apply limit
        if (options?.limit !== undefined) {
            records = records.slice(0, options.limit);
        }

        // Clone records to prevent callers from mutating the store
        records = records.map(record => structuredClone(record));

        // Populate relationships if requested
        if (populate && populate.length > 0) {
            records = await Promise.all(
                records.map(record => this.populateRecord(record, populate, model))
            );
        }

        return records;
    }

    async update(model: Model | string, id: string, data: Record<string, unknown>, staging?: boolean): Promise<Entity> {
        model = this.resolveModel(model);
        data = depopulate(model, data);
        const key = this.storeKey(model.path);

        if (staging) {
            // Staging update: only updates entities in stagingStore
            const stagingCollection = this.stagingStore.get(key);
            if (!stagingCollection) {
                throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
            }

            const record = stagingCollection.get(id);
            if (!record) {
                throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
            }

            // Staging skips write conditions — they're enforced on commit()
            // const writeCondition = this.conditions.write[model.name];
            // if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
            //     throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
            // }

            // Process file uploads (staging)
            const [processedData, fileUrls] = await this.processFiles(data, true);

            const updatedRecord = {
                ...record,
                ...processedData,
                _updatedAt: new Date().toISOString()
            } as Entity;

            stagingCollection.set(id, updatedRecord);

            // Track new file URLs (accumulate)
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, id);
                if (!this.stagingFileUrls.has(fileKey)) {
                    this.stagingFileUrls.set(fileKey, new Set());
                }
                for (const url of fileUrls) {
                    this.stagingFileUrls.get(fileKey)!.add(url);
                }
            }

            return updatedRecord;
        }

        // Check staging first — staging is the working copy and takes priority
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(id);
        if (stagingRecord) {
            const [processedData, fileUrls] = await this.processFiles(data, true);
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, id);
                if (!this.stagingFileUrls.has(fileKey)) this.stagingFileUrls.set(fileKey, new Set());
                for (const url of fileUrls) this.stagingFileUrls.get(fileKey)!.add(url);
            }
            stagingCollection!.set(id, { ...stagingRecord, ...processedData, _updatedAt: new Date().toISOString() } as Entity);
            await this.commit(model, id);
            return this.store.get(key)!.get(id)!;
        }

        const collection = this.store.get(key);
        const record = collection?.get(id);

        if (!record) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Check write condition — throw "not found" if filtered out
        const writeCondition = this.conditions.write[model.name];
        if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Process file uploads
        const [processedData] = await this.processFiles(data);

        // Update the record (partial update)
        const updatedRecord = {
            ...record,
            ...processedData,
            _updatedAt: new Date().toISOString()
        } as Entity;

        collection!.set(id, updatedRecord);

        // Notify subscribers
        this.notifyEntitySubscribers(`${this.storeKey(model.path)}:${id}`);
        this.notifyListSubscribers(this.storeKey(model.path));

        return updatedRecord;
    }

    async delete(model: Model | string, id: string, staging?: boolean): Promise<void> {
        model = this.resolveModel(model);
        const key = this.storeKey(model.path);
        let found = false;

        // Delete from staging store if present
        const stagingCollection = this.stagingStore.get(key);
        if (stagingCollection) {
            const record = stagingCollection.get(id);
            if (record) {
                // Check write condition
                const writeCondition = this.conditions.write[model.name];
                if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
                    throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
                }

                stagingCollection.delete(id);

                // Discard tracked file URLs
                const fileKey = this.stagingFileKey(key, id);
                const urls = this.stagingFileUrls.get(fileKey);
                if (urls) {
                    await this.storage.discard([...urls]);
                    this.stagingFileUrls.delete(fileKey);
                }

                found = true;
            }
        }

        // Delete from real store if present (skip when staging-only)
        if (!staging) {
            const collection = this.store.get(key);
            if (collection) {
                const record = collection.get(id);
                if (record) {
                    // Check write condition — throw "not found" if filtered out
                    const writeCondition = this.conditions.write[model.name];
                    if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
                        throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
                    }

                    collection.delete(id);
                    found = true;
                }
            }
        }

        if (!found) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Remove entity subscriptions for deleted entity
        this.entitySubscriptions.delete(`${key}:${id}`);

        // Notify list subscribers (only when real store was modified)
        if (!staging) {
            this.notifyListSubscribers(key);
        }
    }

    async commit(model: Model | string, id: string): Promise<void> {
        model = this.resolveModel(model);
        const key = this.storeKey(model.path);

        // Get entity from staging
        const stagingCollection = this.stagingStore.get(key);
        if (!stagingCollection) {
            throw new Error(`Record with id '${id}' not found in staging for model '${model.name}'`);
        }

        const stagingEntity = stagingCollection.get(id);
        if (!stagingEntity) {
            throw new Error(`Record with id '${id}' not found in staging for model '${model.name}'`);
        }

        // Enforce write conditions on commit
        const writeCondition = this.conditions.write[model.name];
        if (writeCondition && !this.matchesFilter(stagingEntity as unknown as Record<string, unknown>, writeCondition)) {
            throw new Error(`Write denied: record does not match condition for model '${model.name}'`);
        }

        // Commit staged files and rewrite URLs
        const fileKey = this.stagingFileKey(key, id);
        const urls = this.stagingFileUrls.get(fileKey);
        if (urls) {
            const urlMapping = await this.storage.commit([...urls]);
            this.stagingFileUrls.delete(fileKey);
            const rewritten = this.rewriteUrls(stagingEntity, urlMapping);
            Object.assign(stagingEntity, rewritten);
        }

        // Ensure real collection exists
        if (!this.store.has(key)) {
            this.store.set(key, new Map());
        }
        const realCollection = this.store.get(key)!;

        // Create-or-update in real store (preserve staging timestamps)
        if (realCollection.has(id)) {
            // Update: merge staged data over existing
            const existing = realCollection.get(id)!;
            const merged = {
                ...existing,
                ...stagingEntity,
            } as Entity;
            realCollection.set(id, merged);
        } else {
            // Create new entry
            realCollection.set(id, stagingEntity);
        }

        // Clean up staging
        stagingCollection.delete(id);

        // Notify subscribers
        this.notifyEntitySubscribers(`${key}:${id}`);
        this.notifyListSubscribers(key);
    }

    /**
     * Notifies entity subscribers when a specific entity changes
     */
    private async notifyEntitySubscribers(key: string): Promise<void> {
        const subscriptions = this.entitySubscriptions.get(key);
        if (!subscriptions || subscriptions.size === 0) return;

        // Extract entity id from key (format: "projectId:modelPath:entityId")
        const lastColon = key.lastIndexOf(':');
        const entityId = key.substring(lastColon + 1);

        for (const sub of subscriptions) {
            try {
                // Re-fetch the entity with the same parameters
                const entity = await this.fetchEntity(sub.model, entityId, sub.populate);
                sub.callback(entity);
            } catch (err) {
                if (err instanceof Error && err.message.includes('not found')) continue;
                console.error(`notifyEntitySubscribers error [model=${sub.model.name}, id=${entityId}, populate=${sub.populate?.join(',') ?? 'none'}]:`, err);
            }
        }
    }

    /**
     * Notifies list subscribers when any entity in a model changes
     */
    private async notifyListSubscribers(modelPath: string): Promise<void> {
        const subscriptions = this.listSubscriptions.get(modelPath);
        if (!subscriptions || subscriptions.size === 0) return;

        for (const sub of subscriptions) {
            // Re-run the query with the same parameters
            const records = await this.fetchList(
                sub.model,
                sub.populate,
                sub.filter,
                sub.order,
                sub.options
            );
            sub.callback(records);
        }
    }

    /**
     * Processes data to upload File objects and replace them with URLs.
     * Returns [processedData, fileUrls] tuple.
     */
    private async processFiles(data: Record<string, unknown>, staging?: boolean): Promise<[Record<string, unknown>, string[]]> {
        const fileUrls: string[] = [];
        const visited = new WeakSet();

        const processValue = async (value: unknown): Promise<unknown> => {
            // FileMetadata with pending file upload (created by Image/Pdf components)
            if (value !== null && typeof value === 'object' && !(value instanceof File) && 'file' in value && (value as Record<string, unknown>).file instanceof File) {
                const meta = value as Record<string, unknown>;
                const file = meta.file as File;
                const url = await this.storage.upload(file, staging);
                fileUrls.push(url);
                const { file: _, ...rest } = meta;
                return { ...rest, url };
            }
            if (Array.isArray(value)) {
                if (visited.has(value)) { console.error('processFiles: circular array reference detected, skipping'); return value; }
                visited.add(value);
                return Promise.all(value.map(item => processValue(item)));
            }
            if (value !== null && typeof value === 'object') {
                if (visited.has(value as object)) { console.error('processFiles: circular object reference detected, skipping'); return value; }
                visited.add(value as object);
                const result: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(value)) {
                    result[k] = await processValue(v);
                }
                return result;
            }
            return value;
        };

        const processed = await processValue(data) as Record<string, unknown>;

        return [processed, fileUrls];
    }

    /**
     * Recursively replaces URL strings in an object/array using a mapping.
     */
    private rewriteUrls(obj: unknown, mapping: Record<string, string>, visited = new WeakSet()): unknown {
        if (typeof obj === 'string') return obj in mapping ? mapping[obj] : obj;
        if (Array.isArray(obj)) {
            if (visited.has(obj)) return obj;
            visited.add(obj);
            return obj.map(item => this.rewriteUrls(item, mapping, visited));
        }
        if (obj !== null && typeof obj === 'object') {
            if (visited.has(obj as object)) return obj;
            visited.add(obj as object);
            const result: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(obj)) {
                result[k] = this.rewriteUrls(v, mapping, visited);
            }
            return result;
        }
        return obj;
    }

    /**
     * Gets all populatable properties (reference or object type) from a model
     */
    private getPopulatableProperties(model: Model) {
        return model.properties.filter(p =>
            (p.type === 'reference' && p.reference) ||
            (p.type === 'object' && (p.reference || p.properties))
        );
    }

    /**
     * Expands glob patterns to concrete field instructions
     * Supports both '/' and '.' as separators (e.g., 'author/posts' or 'author.posts')
     * Returns array of { field, nestedPattern } objects
     */
    private expandPattern(pattern: string, model: Model): Array<{ field: string; nestedPattern: string | null }> {
        // Normalize: convert dots to slashes for uniform processing
        const normalized = pattern.replace(/\./g, '/');
        const parts = normalized.split('/');
        const first = parts[0];
        const rest = parts.slice(1).join('/') || null;

        if (first === '**') {
            // Recursive: populate all fields at this level, and recurse with **/*
            const props = this.getPopulatableProperties(model);
            return props.map(p => ({ field: p.id, nestedPattern: rest ? `**/${rest}` : '**/*' }));
        }

        if (first === '*') {
            // Wildcard: populate all reference/object fields at this level
            const props = this.getPopulatableProperties(model);
            return props.map(p => ({ field: p.id, nestedPattern: rest }));
        }

        // Specific field name
        return [{ field: first, nestedPattern: rest }];
    }

    /**
     * Looks up a related record by checking staging first, then real store.
     */
    private lookupRelatedRecord(refModel: Model, id: string): Entity | undefined {
        const refKey = this.storeKey(refModel.path);

        // Check staging first
        const stagingCollection = this.stagingStore.get(refKey);
        const stagingRecord = stagingCollection?.get(id);
        if (stagingRecord) return stagingRecord;

        // Fall back to real store
        const realCollection = this.store.get(refKey);
        return realCollection?.get(id);
    }

    /**
     * Populates relationship fields in a record
     * Supports glob-style patterns: *, *\/*, **\/*
     */
    private async populateRecord(
        record: Entity,
        populate: string[],
        model: Model,
        cache: Map<string, Entity> = new Map()
    ): Promise<Entity> {
        const populated: Entity = { ...record };

        // Cache doubles as visited set — prevents circular references and reuses resolved entities
        const recordKey = `${model.path}:${record.id}`;
        if (cache.has(recordKey)) {
            return cache.get(recordKey)!;
        }
        cache.set(recordKey, populated);

        // Expand all patterns and group by field
        const fieldInstructions = new Map<string, string[]>();

        for (const pattern of populate) {
            const expanded = this.expandPattern(pattern, model);
            for (const { field, nestedPattern } of expanded) {
                if (!fieldInstructions.has(field)) {
                    fieldInstructions.set(field, []);
                }
                if (nestedPattern) {
                    fieldInstructions.get(field)!.push(nestedPattern);
                }
            }
        }

        // Populate each field
        for (const [fieldName, nestedPatterns] of fieldInstructions) {
            // Match on id (field key) or name (for backwards compatibility)
            const property = model.properties.find(p => p.id === fieldName || p.name === fieldName);
            if (!property) continue;

            // Records use property.name as keys
            const value = (record as unknown as Record<string, unknown>)[property.name];
            if (value === undefined || value === null) continue;

            if ((property.type === 'reference' || (property.type === 'object' && property.reference)) && property.reference) {
                // Read condition for the referenced model
                const refReadCondition = this.conditions.read[property.reference.name];

                if (Array.isArray(value)) {
                    // Array of references
                    const populatedArray = await Promise.all(
                        value.map(async (refId: string | Entity) => {
                            // Handle already-populated references (when re-populating)
                            const id = typeof refId === 'object' && refId !== null && 'id' in refId
                                ? refId.id
                                : refId;

                            const relatedRecord = this.lookupRelatedRecord(property.reference!, id as string);
                            if (!relatedRecord) {
                                return null;
                            }

                            // Check read condition on referenced model
                            if (refReadCondition && !this.matchesFilter(relatedRecord as unknown as Record<string, unknown>, refReadCondition)) {
                                return null;
                            }

                            if (nestedPatterns.length > 0) {
                                return await this.populateRecord(
                                    relatedRecord, nestedPatterns, property.reference!, cache
                                );
                            }
                            return relatedRecord;
                        })
                    );
                    (populated as unknown as Record<string, unknown>)[property.name] = populatedArray.filter(r => r !== null);
                } else {
                    // Single reference - handle already-populated references
                    const id = typeof value === 'object' && value !== null && 'id' in value
                        ? (value as Entity).id
                        : value as string;

                    const relatedRecord = this.lookupRelatedRecord(property.reference!, id);
                    if (relatedRecord) {
                        // Check read condition on referenced model
                        if (refReadCondition && !this.matchesFilter(relatedRecord as unknown as Record<string, unknown>, refReadCondition)) {
                            (populated as unknown as Record<string, unknown>)[property.name] = null;
                        } else if (nestedPatterns.length > 0) {
                            (populated as unknown as Record<string, unknown>)[property.name] = await this.populateRecord(
                                relatedRecord, nestedPatterns, property.reference!, cache
                            );
                        } else {
                            (populated as unknown as Record<string, unknown>)[property.name] = relatedRecord;
                        }
                    } else {
                        console.error(`Referenced ${property.reference!.name} with id '${id}' not found`);
                        (populated as unknown as Record<string, unknown>)[property.name] = null;
                    }
                }
            } else if (property.type === 'object' && property.properties) {
                // Object type with nested properties - just include as-is
                // The nested properties are already part of the data
                // But if there are nested patterns, we might need to populate references within
                if (nestedPatterns.length > 0 && typeof value === 'object') {
                    // Create a pseudo-model for the nested object
                    const nestedModel: Model = {
                        id: `${model.id}_${recordKey}`,
                        name: `${model.name}_${recordKey}`,
                        type: 'singleton' as const,
                        path: '',
                        properties: property.properties,
                        actions: []
                    };

                    if (Array.isArray(value)) {
                        const populatedArray = await Promise.all(
                            value.map(async (item: Entity) => {
                                if (item && typeof item === 'object' && 'id' in item) {
                                    return await this.populateRecord(item, nestedPatterns, nestedModel, cache);
                                }
                                return item;
                            })
                        );
                        (populated as unknown as Record<string, unknown>)[property.name] = populatedArray;
                    } else if ('id' in (value as object)) {
                        (populated as unknown as Record<string, unknown>)[property.name] = await this.populateRecord(
                            value as Entity, nestedPatterns, nestedModel, cache
                        );
                    }
                }
            }
        }

        return populated;
    }
}
